/**
 * Variant Skeleton Loader
 * 
 * Populates skeleton placeholder cards with real content as user scrolls.
 * Uses Shopify's Section Rendering API to fetch individual product cards.
 */

(function() {
  'use strict';

  const CONFIG = {
    batchSize: 8, // Load 8 cards at a time to reduce skeleton population
    rootMargin: '800px', // Start loading much earlier (was 200px)
    debounceMs: 50 // Reduced debounce for faster response
  };

  let observer = null;
  let loadingQueue = new Set();
  let isProcessing = false;

  /**
   * Fetch product card HTML from Shopify
   */
  async function fetchProductCard(productHandle, variantId) {
    try {
      // Use section rendering to get just the product card
      // Add cache buster and priority hint for faster loading
      const url = `/products/${productHandle}?section_id=variant-card-render&variant=${variantId}&_=${Date.now()}`;
      const response = await fetch(url, {
        // Add priority hint for faster loading
        priority: 'high',
        // Don't wait for credentials
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const html = await response.text();
      return html;
    } catch (error) {
      return null;
    }
  }

  /**
   * Populate a skeleton with real card content
   */
  async function populateSkeleton(skeleton) {
    if (!(skeleton instanceof HTMLElement)) return;
    
    const productHandle = skeleton.dataset.productHandle;
    const variantId = skeleton.dataset.variantId;
    const productId = skeleton.dataset.productId;
    
    if (!productHandle || !variantId) {
      return;
    }
    
    // Check if already loading or populated
    if (loadingQueue.has(variantId) || skeleton.classList.contains('populated')) {
      return;
    }
    
    // Mark as loading
    skeleton.classList.add('loading');
    loadingQueue.add(variantId);
    
    try {
      const cardHtml = await fetchProductCard(productHandle, variantId);
      
      if (!cardHtml) {
        skeleton.classList.remove('product-grid__item--hidden');
        skeleton.classList.add('active');
        return;
      }
      
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(cardHtml, 'text/html');
      
      // Find the product card - try multiple selectors
      let card = doc.querySelector('li.product-grid__item[data-variant-id="' + variantId + '"]');
      if (!card) {
        card = doc.querySelector('.sunice-variant-product-card, .sunice-product-card');
      }
      if (!card) {
        card = doc.querySelector('.product-card');
      }
      
      if (card) {
        // Get the inner content (the actual card div, not the li wrapper)
        let cardContent = card.querySelector('.sunice-variant-product-card, .sunice-product-card, .product-card');
        
        // Replace skeleton content with real card
        skeleton.innerHTML = '';
        
        if (cardContent) {
          // Found the card content inside the li - append it
          skeleton.appendChild(cardContent);
        } else if (card.tagName === 'LI' && card.children.length > 0) {
          // Card is an li with children - move all children to skeleton
          while (card.firstChild) {
            skeleton.appendChild(card.firstChild);
          }
        } else {
          // Card is the content itself - append it
          skeleton.appendChild(card);
        }
        
        // Mark as populated
        skeleton.classList.remove('loading');
        skeleton.classList.add('populated');
        skeleton.removeAttribute('data-skeleton');
        
        // Reveal the card
        skeleton.classList.remove('product-grid__item--hidden');
        skeleton.classList.add('active');
        skeleton.style.setProperty('display', 'block', 'important');
        skeleton.style.setProperty('visibility', 'visible', 'important');
        skeleton.style.setProperty('opacity', '1', 'important');
        
        // Initialize components
        initializeCardComponents(skeleton);
      } else {
        // Fallback: reveal skeleton
        skeleton.classList.remove('product-grid__item--hidden');
        skeleton.classList.add('active');
      }
    } catch (error) {
      skeleton.classList.remove('product-grid__item--hidden');
      skeleton.classList.add('active');
    } finally {
      loadingQueue.delete(variantId);
      skeleton.classList.remove('loading');
    }
  }

  /**
   * Initialize components for newly loaded card
   */
  function initializeCardComponents(card) {
    const globalWindow = /** @type {any} */ (window);
    
    // Initialize color swipers
    if (globalWindow.SuniceColorSwiperInstance) {
      const swiperContainers = card.querySelectorAll('.color-swiper-container');
      swiperContainers.forEach(container => {
        if (container instanceof HTMLElement && 
            container.dataset.suniceColorSwiperInitialized !== 'true') {
          globalWindow.SuniceColorSwiperInstance.initializeColorSwiper(container);
        }
      });
    }
    
    // Initialize variant pickers
    if (globalWindow.setupVariantCardPickers) {
      globalWindow.setupVariantCardPickers();
    }
    
    // Trigger lazy load for images
    const images = card.querySelectorAll('img[data-src]');
    images.forEach(img => {
      if (img instanceof HTMLImageElement && img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
  }

  /**
   * Process visible skeletons
   */
  async function processSkeletons(entries) {
    if (isProcessing) return;
    isProcessing = true;
    
    // Get all skeletons that are intersecting or near viewport
    const visibleSkeletons = entries
      .filter(entry => {
        // Include if intersecting OR if it's a skeleton element (for manual processing)
        if (entry.isIntersecting) return true;
        if (entry.target instanceof HTMLElement) {
          const rect = entry.target.getBoundingClientRect();
          // Also include if within 800px of viewport
          return rect.top < window.innerHeight + 800 && rect.bottom > -800;
        }
        return false;
      })
      .map(entry => entry.target)
      .filter(el => el instanceof HTMLElement && el.hasAttribute('data-skeleton') && !loadingQueue.has(el.dataset.variantId));
    
    if (visibleSkeletons.length === 0) {
      isProcessing = false;
      return;
    }
    
    // Process in batches
    const batch = visibleSkeletons.slice(0, CONFIG.batchSize);
    
    // Populate all in parallel - no delays, immediate processing
    // Use Promise.allSettled to ensure all requests complete even if some fail
    await Promise.allSettled(batch.map(skeleton => populateSkeleton(skeleton)));
    
    isProcessing = false;
    
    // Update product count
    updateProductCount();
  }

  /**
   * Update visible product count
   */
  function updateProductCount() {
    const visibleCards = document.querySelectorAll('.product-grid__item.active:not(.filter-hidden)');
    const count = visibleCards.length;
    
    const countEls = document.querySelectorAll('.products-count-wrapper span');
    countEls.forEach(el => {
      if (el instanceof HTMLElement) {
        const txt = (el.textContent || '').trim();
        if (/\d/.test(txt)) {
          el.textContent = txt.replace(/\d[\d,]*/, String(count));
        }
      }
    });
  }

  /**
   * Initialize the skeleton loader
   */
  function init() {
    // Ensure scroll listener is always active FIRST
    if (!window._skeletonScrollListenerAdded) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      window._skeletonScrollListenerAdded = true;
    }
    
    // Call reinit() with the SAME delay pattern as facets:update event (200ms)
    // This ensures DOM is ready, just like when filters are clicked
    setTimeout(() => {
      reinit();
      // Also check on scroll after reinit
      setTimeout(checkSkeletonsOnScroll, 500);
    }, 200);
    
    // Additional delayed calls to ensure skeletons are found
    setTimeout(() => {
      reinit();
      setTimeout(checkSkeletonsOnScroll, 300);
    }, 600);
    
    setTimeout(() => {
      reinit();
      setTimeout(checkSkeletonsOnScroll, 300);
    }, 1200);
  }

  /**
   * Reinitialize after filter changes - THIS IS THE FUNCTION THAT WORKS!
   */
  function reinit() {
    if (observer) {
      observer.disconnect();
    }
    loadingQueue.clear();
    isProcessing = false;
    
    // Find all skeletons
    const skeletons = document.querySelectorAll('.product-grid__item--skeleton[data-skeleton="true"]');
    
    if (skeletons.length > 0) {
      // Recreate observer with larger rootMargin for earlier loading
      observer = new IntersectionObserver(processSkeletons, {
        rootMargin: '1500px', // Increased from 1000px for much earlier loading
        threshold: 0
      });
      
      skeletons.forEach(skeleton => {
        observer.observe(skeleton);
      });
      
      // Process skeletons near viewport - much more aggressive loading
      // Batch all getBoundingClientRect calls together to minimize forced reflows
      const windowHeight = window.innerHeight;
      const viewportSkeletons = Array.from(skeletons).filter(skeleton => {
        const rect = skeleton.getBoundingClientRect();
        return rect.top < windowHeight + 2000 && rect.bottom > -2000; // Increased range
      });
      
      // Also get page 2 skeletons (for hidden ones) - ALWAYS get them
      const page2Skeletons = Array.from(skeletons).filter(s => {
        const page = parseInt(s.dataset.page || '1', 10);
        return page === 2 && !s.classList.contains('populated');
      });
      
      // ALWAYS prioritize page 2 skeletons on initial load (when scrollTop is 0)
      // This ensures page 2 loads immediately, just like when filters are clicked
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isInitialLoad = scrollTop === 0;
      
      let skeletonsToProcess = [];
      if (isInitialLoad && page2Skeletons.length > 0) {
        // On initial load, prioritize page 2 skeletons - load ALL of them immediately
        skeletonsToProcess = page2Skeletons;
      } else if (viewportSkeletons.length > 0) {
        // Otherwise, use viewport skeletons
        skeletonsToProcess = viewportSkeletons;
      } else if (page2Skeletons.length > 0) {
        // Fallback to page 2 if no viewport skeletons
        skeletonsToProcess = page2Skeletons;
      }
      
      if (skeletonsToProcess.length > 0) {
        // Process immediately, no delay
        processSkeletons(skeletonsToProcess.slice(0, CONFIG.batchSize).map(s => ({ target: s, isIntersecting: true })));
      }
    }
  }

  /**
   * Check for skeletons on scroll (fallback if IntersectionObserver misses them)
   */
  function checkSkeletonsOnScroll() {
    // ALWAYS find skeletons on every scroll check
    let skeletons = document.querySelectorAll('.product-grid__item--skeleton[data-skeleton="true"]');
    
    // Filter out populated ones
    skeletons = Array.from(skeletons).filter(s => !s.classList.contains('populated'));
    
    if (skeletons.length === 0) {
      return;
    }
    
    // Batch all scroll/geometry reads together to avoid forced reflows
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    
    // Group by page
    const page2Skeletons = skeletons.filter(s => parseInt(s.dataset.page || '1', 10) === 2);
    const page3PlusSkeletons = skeletons.filter(s => parseInt(s.dataset.page || '1', 10) >= 3);
    
    // VERY AGGRESSIVE: Load page 2 immediately on initial load OR if scrolled at all OR near bottom
    // This ensures page 2 loads on page load, just like when filters are clicked
    const isInitialLoad = scrollTop === 0;
    const shouldLoadPage2 = isInitialLoad || scrollTop > 0 || distanceFromBottom < 5000; // Increased from 3000px
    
    // Load page 3+ when near bottom - much earlier
    const shouldLoadPage3Plus = distanceFromBottom < 4000; // Increased from 2500px
    
    let skeletonsToLoad = [];
    
    if (shouldLoadPage2 && page2Skeletons.length > 0) {
      skeletonsToLoad = page2Skeletons.slice(0, CONFIG.batchSize);
    } else if (shouldLoadPage3Plus && page3PlusSkeletons.length > 0) {
      skeletonsToLoad = page3PlusSkeletons.slice(0, CONFIG.batchSize);
    }
    
    if (skeletonsToLoad.length > 0 && !isProcessing) {
      processSkeletons(skeletonsToLoad.map(s => ({ target: s, isIntersecting: true })));
    }
  }

  // Throttled scroll handler - ALWAYS check for skeletons on scroll
  let scrollTimeout = null;
  let lastScrollTop = 0;
  function handleScroll() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Check on ANY scroll movement (no threshold) - faster response
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(() => {
      // Always check for skeletons on scroll
      checkSkeletonsOnScroll();
      scrollTimeout = null;
    }, 50); // Reduced from 150ms to 50ms for faster response
    
    lastScrollTop = currentScrollTop;
  }

  // Initialize on DOM ready with multiple retries
  function initializeWithRetry(retryCount = 0) {
    const maxRetries = 10; // Increased retries
    const skeletons = document.querySelectorAll('.product-grid__item--skeleton[data-skeleton="true"]');
    
    // Always add scroll listener, even if no skeletons found yet
    if (!window._skeletonScrollListenerAdded) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      window._skeletonScrollListenerAdded = true;
    }
    
    if (skeletons.length === 0 && retryCount < maxRetries) {
      // Keep retrying, but also check on scroll
      setTimeout(() => initializeWithRetry(retryCount + 1), 300);
      // Also check immediately in case skeletons exist but selector is wrong
      checkSkeletonsOnScroll();
      return;
    }
    
    // Found skeletons or max retries reached
    if (skeletons.length > 0) {
      init();
    }
    
    // Always check on load and after delays, even if no skeletons found initially
    // More aggressive checking to match facets:update behavior
    checkSkeletonsOnScroll(); // Check immediately
    setTimeout(checkSkeletonsOnScroll, 100); // Check after 100ms
    setTimeout(checkSkeletonsOnScroll, 300); // Check after 300ms
    setTimeout(checkSkeletonsOnScroll, 600); // Check after 600ms
    setTimeout(checkSkeletonsOnScroll, 1000); // Check after 1s
    setTimeout(checkSkeletonsOnScroll, 2000); // Check after 2s
    setTimeout(checkSkeletonsOnScroll, 3000); // Check after 3s
    
    // Check if page is already scrolled
    if (window.pageYOffset > 0) {
      setTimeout(checkSkeletonsOnScroll, 50);
    }
    
    // Also trigger on any scroll event after a delay (simulate user scrolling)
    setTimeout(() => {
      // Trigger a fake scroll event to activate the handler
      window.dispatchEvent(new Event('scroll'));
    }, 500);
  }

  // Initialize on multiple events to catch all scenarios
  function startInitialization() {
    setTimeout(initializeWithRetry, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startInitialization);
  } else {
    startInitialization();
  }
  
  // Also initialize on window load (after all resources loaded)
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => {
      setTimeout(() => {
        initializeWithRetry(0); // Reset retry count
      }, 100);
    });
  } else {
    // Already loaded, check immediately
    setTimeout(() => {
      initializeWithRetry(0);
    }, 100);
  }

  // Reinitialize on filter changes
  document.addEventListener('facets:update', () => {
    setTimeout(() => {
      reinit();
      // Ensure scroll listener is active
      if (!window._skeletonScrollListenerAdded) {
        window.addEventListener('scroll', handleScroll, { passive: true });
        window._skeletonScrollListenerAdded = true;
      }
      setTimeout(checkSkeletonsOnScroll, 500);
    }, 200);
  });
  window.addEventListener('popstate', () => {
    setTimeout(() => {
      reinit();
      if (!window._skeletonScrollListenerAdded) {
        window.addEventListener('scroll', handleScroll, { passive: true });
        window._skeletonScrollListenerAdded = true;
      }
      setTimeout(checkSkeletonsOnScroll, 500);
    }, 200);
  });

  // Expose globally
  window.VariantSkeletonLoader = {
    init,
    reinit,
    populateSkeleton
  };

})();

